import type { ReactNode } from "react";
import { Box, Button, Container, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

interface Props {
  body?: string;
  buttonPrimaryLink?: string;
  buttonPrimaryTitle?: string;
  buttonSecondaryLink?: string;
  buttonSecondaryTitle?: string;
  children?: ReactNode;
  image?: string;
  isCentered?: boolean;
  isFullWidth?: boolean;
  title?: string;
}
export const LandingHero = ({
  body,
  buttonPrimaryLink,
  buttonPrimaryTitle,
  buttonSecondaryLink,
  buttonSecondaryTitle,
  children,
  title,
  image,
  isCentered = false,
  isFullWidth = false,
}: Props) => {
  const StyledRoot = styled("div")(({ theme }) => ({
    position: "relative",
    backgroundImage: `url(${image})`,
    backgroundSize: "cover",
    backgroundPosition: "center right",
    padding: theme.spacing(10, 0),
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(15, 0),
    },
    height: "100%",
    zIndex: 0,
    "&:before": {
      background: isCentered
        ? "linear-gradient(179deg, rgba(0, 0, 0, 0.50) 1.1%, rgba(0, 0, 0, 0.00) 120.77%)"
        : "linear-gradient(97deg, rgba(0, 0, 0, 0.50) 25.03%, rgba(0, 0, 0, 0.00) 71.24%)",
      [theme.breakpoints.down("md")]: {
        background: "rgba(0, 0, 0, 0.50)",
      },
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: -1,
      pointerEvents: "none",
    },
  }));

  const renderContent = () => (
    <Stack
      direction={"column"}
      spacing={2}
      sx={[isCentered && { textAlign: "center" }, { height: "100%" }]}
      alignItems={isCentered ? "center" : "flex-start"}
    >
      <Typography
        color="primary.contrastText"
        variant="h2"
        sx={{
          display: "-webkit-box",
          overflow: "hidden",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
          maxWidth: "560px",
          fontWeight: {
            sm: 500,
            md: 600,
          },
        }}
      >
        {!title ? <span style={{ lineHeight: 1 }}>&nbsp;</span> : title}
      </Typography>

      {body ? (
        <Typography
          color="primary.contrastText"
          variant="bodyLg"
          sx={{
            display: "-webkit-box",
            overflow: "hidden",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 4,
            maxWidth: {
              xs: "100%",
              sm: "80%",
              md: "60%",
            },
          }}
        >
          {body}
        </Typography>
      ) : null}

      {children}

      <Stack spacing={2} direction={{ xs: "column", sm: "row" }} sx={{ pt: 2 }}>
        <Button
          sx={{
            opacity: buttonPrimaryLink ? 1 : 0,
          }}
          href={buttonPrimaryLink}
          size="large"
          variant="contained"
          color="primary"
        >
          {buttonPrimaryTitle || <>&nbsp;</>}
        </Button>

        <Button
          href={buttonSecondaryLink}
          size="large"
          variant="outlined"
          color="secondary"
          sx={{
            opacity: buttonSecondaryLink ? 1 : 0,
            borderColor: "primary.contrastText",
            color: "primary.contrastText",
            "&:hover": {
              borderColor: "primary.contrastText",
            },
          }}
        >
          {buttonSecondaryTitle || <>&nbsp;</>}
        </Button>
      </Stack>
    </Stack>
  );

  if (isFullWidth) {
    return (
      <StyledRoot>
        <Container sx={{ height: "100%" }} maxWidth="lg">
          {renderContent()}
        </Container>
      </StyledRoot>
    );
  }

  return (
    <StyledRoot>
      <Box sx={{ px: 3, height: "100%" }}>{renderContent()}</Box>
    </StyledRoot>
  );
};
